import React from "react";
import SectionCard from "../components/SectionCard";
import {
  BilingualTableHeader,
  BilingualTextRow,
  BilingualImageRow,
} from "../components/BilingualFormFields";
import ReadOnlyBadge from "../components/ReadOnlyBadge";
import { getError } from "../utils/validation";

export function countSectionErrors(errors, prefixes) {
  return Object.keys(errors).filter((key) => {
    const n = key.replace(/^(en|es)\./, "");
    return prefixes.some((p) => n.startsWith(p));
  }).length;
}

export function MarketingHeroSection({
  id,
  title,
  description,
  heroEn,
  heroEs,
  setHeroEn,
  setHeroEs,
  errors,
  onImageUpload,
  uploadingKey,
  prefix = "hero",
}) {
  return (
    <SectionCard
      id={id}
      title={title}
      description={description}
      errorCount={countSectionErrors(errors, [`${prefix}.`])}
      defaultOpen
    >
      <BilingualImageRow
        label="Background image"
        fieldKey={`${prefix}.backgroundImage`}
        srcEn={heroEn.backgroundImage}
        altEn=""
        srcEs={heroEs.backgroundImage}
        altEs=""
        onSrcEn={(v) => setHeroEn({ ...heroEn, backgroundImage: v })}
        onAltEn={() => {}}
        onSrcEs={(v) => setHeroEs({ ...heroEs, backgroundImage: v })}
        onAltEs={() => {}}
        onUploadEn={(f) =>
          onImageUpload("en", `${prefix}.bg`, f, (url) => setHeroEn({ ...heroEn, backgroundImage: url }))
        }
        onUploadEs={(f) =>
          onImageUpload("es", `${prefix}.bg`, f, (url) => setHeroEs({ ...heroEs, backgroundImage: url }))
        }
        srcErrorEn={getError(`${prefix}.backgroundImage`, errors, "en")}
        srcErrorEs={getError(`${prefix}.backgroundImage`, errors, "es")}
        uploadingEn={uploadingKey === `en.${prefix}.bg`}
        uploadingEs={uploadingKey === `es.${prefix}.bg`}
        showAlt={false}
      />
      <BilingualTableHeader />
      <BilingualTextRow
        label="Headline"
        fieldKey={`${prefix}.headline`}
        valueEn={heroEn.headline}
        valueEs={heroEs.headline}
        onChangeEn={(v) => setHeroEn({ ...heroEn, headline: v })}
        onChangeEs={(v) => setHeroEs({ ...heroEs, headline: v })}
        errorEn={getError(`${prefix}.headline`, errors, "en")}
        errorEs={getError(`${prefix}.headline`, errors, "es")}
        maxLength={200}
      />
      <BilingualTextRow
        label="Description"
        fieldKey={`${prefix}.description`}
        valueEn={heroEn.description}
        valueEs={heroEs.description}
        onChangeEn={(v) => setHeroEn({ ...heroEn, description: v })}
        onChangeEs={(v) => setHeroEs({ ...heroEs, description: v })}
        errorEn={getError(`${prefix}.description`, errors, "en")}
        errorEs={getError(`${prefix}.description`, errors, "es")}
        multiline
        rows={3}
        maxLength={800}
      />
      <BilingualTextRow
        label="CTA label"
        fieldKey={`${prefix}.cta.label`}
        valueEn={heroEn.cta && heroEn.cta.label}
        valueEs={heroEs.cta && heroEs.cta.label}
        onChangeEn={(v) => setHeroEn({ ...heroEn, cta: { ...(heroEn.cta || {}), label: v } })}
        onChangeEs={(v) => setHeroEs({ ...heroEs, cta: { ...(heroEs.cta || {}), label: v } })}
        errorEn={getError(`${prefix}.cta.label`, errors, "en")}
        errorEs={getError(`${prefix}.cta.label`, errors, "es")}
        maxLength={40}
      />
      <BilingualTextRow
        label="CTA link"
        fieldKey={`${prefix}.cta.href`}
        valueEn={heroEn.cta && heroEn.cta.href}
        valueEs={heroEs.cta && heroEs.cta.href}
        onChangeEn={(v) => setHeroEn({ ...heroEn, cta: { ...(heroEn.cta || {}), href: v } })}
        onChangeEs={(v) => setHeroEs({ ...heroEs, cta: { ...(heroEs.cta || {}), href: v } })}
        errorEn={getError(`${prefix}.cta.href`, errors, "en")}
        errorEs={getError(`${prefix}.cta.href`, errors, "es")}
      />
    </SectionCard>
  );
}

export function ProgramCtaSection({
  ctaEn,
  ctaEs,
  setCtaEn,
  setCtaEs,
  errors,
  onImageUpload,
  uploadingKey,
  prefix = "programCta",
}) {
  return (
    <SectionCard
      id="programCta"
      title="Program CTA"
      description="Bottom gradient card"
      errorCount={countSectionErrors(errors, [`${prefix}.`])}
      collapsible
    >
      <BilingualTextRow
        label="Title"
        fieldKey={`${prefix}.title`}
        valueEn={ctaEn.title}
        valueEs={ctaEs.title}
        onChangeEn={(v) => setCtaEn({ ...ctaEn, title: v })}
        onChangeEs={(v) => setCtaEs({ ...ctaEs, title: v })}
        errorEn={getError(`${prefix}.title`, errors, "en")}
        errorEs={getError(`${prefix}.title`, errors, "es")}
      />
      <BilingualTextRow
        label="Description"
        fieldKey={`${prefix}.description`}
        valueEn={ctaEn.description}
        valueEs={ctaEs.description}
        onChangeEn={(v) => setCtaEn({ ...ctaEn, description: v })}
        onChangeEs={(v) => setCtaEs({ ...ctaEs, description: v })}
        multiline
        rows={2}
        errorEn={getError(`${prefix}.description`, errors, "en")}
        errorEs={getError(`${prefix}.description`, errors, "es")}
      />
      <BilingualTextRow
        label="Button label"
        fieldKey={`${prefix}.cta.label`}
        valueEn={ctaEn.cta && ctaEn.cta.label}
        valueEs={ctaEs.cta && ctaEs.cta.label}
        onChangeEn={(v) => setCtaEn({ ...ctaEn, cta: { ...ctaEn.cta, label: v } })}
        onChangeEs={(v) => setCtaEs({ ...ctaEs, cta: { ...ctaEs.cta, label: v } })}
        errorEn={getError(`${prefix}.cta.label`, errors, "en")}
        errorEs={getError(`${prefix}.cta.label`, errors, "es")}
      />
      <BilingualTextRow
        label="Button href"
        fieldKey={`${prefix}.cta.href`}
        valueEn={ctaEn.cta && ctaEn.cta.href}
        valueEs={ctaEs.cta && ctaEs.cta.href}
        onChangeEn={(v) => setCtaEn({ ...ctaEn, cta: { ...ctaEn.cta, href: v } })}
        onChangeEs={(v) => setCtaEs({ ...ctaEs, cta: { ...ctaEs.cta, href: v } })}
        errorEn={getError(`${prefix}.cta.href`, errors, "en")}
        errorEs={getError(`${prefix}.cta.href`, errors, "es")}
      />
      <BilingualImageRow
        label="Image"
        fieldKey={`${prefix}.image`}
        srcEn={ctaEn.image && ctaEn.image.src}
        altEn={ctaEn.image && ctaEn.image.alt}
        srcEs={ctaEs.image && ctaEs.image.src}
        altEs={ctaEs.image && ctaEs.image.alt}
        onSrcEn={(v) => setCtaEn({ ...ctaEn, image: { ...ctaEn.image, src: v } })}
        onAltEn={(v) => setCtaEn({ ...ctaEn, image: { ...ctaEn.image, alt: v } })}
        onSrcEs={(v) => setCtaEs({ ...ctaEs, image: { ...ctaEs.image, src: v } })}
        onAltEs={(v) => setCtaEs({ ...ctaEs, image: { ...ctaEs.image, alt: v } })}
        onUploadEn={(f) =>
          onImageUpload("en", `${prefix}.img`, f, (url) =>
            setCtaEn({ ...ctaEn, image: { ...ctaEn.image, src: url } })
          )
        }
        onUploadEs={(f) =>
          onImageUpload("es", `${prefix}.img`, f, (url) =>
            setCtaEs({ ...ctaEs, image: { ...ctaEs.image, src: url } })
          )
        }
        srcErrorEn={getError(`${prefix}.image.src`, errors, "en")}
        altErrorEn={getError(`${prefix}.image.alt`, errors, "en")}
        srcErrorEs={getError(`${prefix}.image.src`, errors, "es")}
        altErrorEs={getError(`${prefix}.image.alt`, errors, "es")}
        uploadingEn={uploadingKey === `en.${prefix}.img`}
        uploadingEs={uploadingKey === `es.${prefix}.img`}
      />
    </SectionCard>
  );
}

export function HexFeaturesSection({
  featuresEn,
  featuresEs,
  setFeaturesEn,
  setFeaturesEs,
  errors,
  onImageUpload,
  uploadingKey,
  prefix = "features",
  sectionId,
}) {
  const updateEn = (index, patch) => {
    const next = featuresEn.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setFeaturesEn(next);
  };
  const updateEs = (index, patch) => {
    const next = featuresEs.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setFeaturesEs(next);
  };

  return (
    <SectionCard
      id={sectionId || prefix}
      title="Feature blocks"
      errorCount={countSectionErrors(errors, [`${prefix}.`])}
      collapsible
    >
      {featuresEn.map((feature, index) => (
        <div key={feature.id} className="cms-bilingual-block">
          <ReadOnlyBadge id={feature.id} />
          <BilingualTextRow
            label="Title"
            fieldKey={`${prefix}.${index}.title`}
            valueEn={feature.title}
            valueEs={(featuresEs[index] && featuresEs[index].title) || ""}
            onChangeEn={(v) => updateEn(index, { title: v })}
            onChangeEs={(v) => updateEs(index, { title: v })}
            errorEn={getError(`${prefix}.${index}.title`, errors, "en")}
            errorEs={getError(`${prefix}.${index}.title`, errors, "es")}
          />
          <BilingualTextRow
            label="Paragraph 1"
            fieldKey={`${prefix}.${index}.paragraphs`}
            valueEn={(feature.paragraphs && feature.paragraphs[0]) || ""}
            valueEs={(featuresEs[index] && featuresEs[index].paragraphs && featuresEs[index].paragraphs[0]) || ""}
            onChangeEn={(v) => {
              const paragraphs = [...(feature.paragraphs || [])];
              paragraphs[0] = v;
              updateEn(index, { paragraphs });
            }}
            onChangeEs={(v) => {
              const paragraphs = [...((featuresEs[index] && featuresEs[index].paragraphs) || [])];
              paragraphs[0] = v;
              updateEs(index, { paragraphs });
            }}
            multiline
            rows={3}
            errorEn={getError(`${prefix}.${index}.paragraphs`, errors, "en")}
            errorEs={getError(`${prefix}.${index}.paragraphs`, errors, "es")}
          />
          <BilingualImageRow
            label="Hex image"
            fieldKey={`${prefix}.${index}.image`}
            srcEn={feature.image && feature.image.src}
            altEn={feature.image && feature.image.alt}
            srcEs={featuresEs[index] && featuresEs[index].image && featuresEs[index].image.src}
            altEs={featuresEs[index] && featuresEs[index].image && featuresEs[index].image.alt}
            onSrcEn={(v) => updateEn(index, { image: { ...feature.image, src: v } })}
            onAltEn={(v) => updateEn(index, { image: { ...feature.image, alt: v } })}
            onSrcEs={(v) =>
              updateEs(index, {
                image: { ...(featuresEs[index] && featuresEs[index].image), src: v },
              })
            }
            onAltEs={(v) =>
              updateEs(index, {
                image: { ...(featuresEs[index] && featuresEs[index].image), alt: v },
              })
            }
            onUploadEn={(f) =>
              onImageUpload("en", `${prefix}.${index}`, f, (url) =>
                updateEn(index, { image: { ...feature.image, src: url } })
              )
            }
            onUploadEs={(f) =>
              onImageUpload("es", `${prefix}.${index}`, f, (url) =>
                updateEs(index, {
                  image: { ...(featuresEs[index] && featuresEs[index].image), src: url },
                })
              )
            }
            srcErrorEn={getError(`${prefix}.${index}.image.src`, errors, "en")}
            altErrorEn={getError(`${prefix}.${index}.image.alt`, errors, "en")}
            srcErrorEs={getError(`${prefix}.${index}.image.src`, errors, "es")}
            altErrorEs={getError(`${prefix}.${index}.image.alt`, errors, "es")}
            uploadingEn={uploadingKey === `en.${prefix}.${index}`}
            uploadingEs={uploadingKey === `es.${prefix}.${index}`}
          />
        </div>
      ))}
    </SectionCard>
  );
}
